import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PageTemplate from '../../components/Common/PageTemplate';
import { 
    Award, 
    Search, 
    Filter, 
    Star, 
    BookOpen, 
    GraduationCap,
    Users,
    Heart,
    Calculator,
    FlaskConical,
    BarChart3,
    User
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const RetiredFaculty = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  const retiredFacultyData = t('retiredFaculty.departments', { returnObjects: true });
  const departments = t('retiredFaculty.departmentList', { returnObjects: true });
  const translations = {
    title: t('retiredFaculty.title'),
    subtitle: t('retiredFaculty.subtitle'),
    introTitle: t('retiredFaculty.intro.title'),
    introP1: t('retiredFaculty.intro.p1'),
    introP2: t('retiredFaculty.intro.p2'),
    totalRetired: t('retiredFaculty.stats.totalRetired'),
    numDepartments: t('retiredFaculty.stats.numDepartments'),
    academicLegacy: t('retiredFaculty.stats.academicLegacy'),
    searchPlaceholder: t('retiredFaculty.searchPlaceholder'),
    resultsFound: t('retiredFaculty.resultsFound'),
    noResultsTitle: t('retiredFaculty.noResults.title'),
    noResultsMessage: t('retiredFaculty.noResults.message'),
    gratitudeTitle: t('retiredFaculty.gratitude.title'),
    gratitudeMessage: t('retiredFaculty.gratitude.message'),
    retiredMember: t('retiredFaculty.retiredMember'),
  };

  const departmentIcons = {
    aktuerya: Calculator,
    biyoloji: Heart,
    istatistik: BarChart3,
    kimya: FlaskConical,
    matematik: BookOpen
  };

  const filteredDepartments = selectedDepartment === 'all' 
      ? Object.entries(retiredFacultyData || {})
      : (retiredFacultyData && retiredFacultyData[selectedDepartment]) ? [[selectedDepartment, retiredFacultyData[selectedDepartment]]] : [];

  const getFilteredMembers = (members) => {
      if (!members) return [];
      if (!searchTerm) return members;
      return members.filter(member => 
          member.toLowerCase().includes(searchTerm.toLowerCase())
      );
  };

  const getTotalCount = () => {
      if (!retiredFacultyData || typeof retiredFacultyData !== 'object') {
        return 0;
      }
      if (selectedDepartment === 'all') {
          return Object.values(retiredFacultyData).reduce((total, dept) => total + (dept.members ? dept.members.length : 0), 0);
      }
      return retiredFacultyData[selectedDepartment]?.members.length || 0;
  };

  const getFilteredCount = () => {
    if (!retiredFacultyData || typeof retiredFacultyData !== 'object') {
        return 0;
    }
    if (selectedDepartment === 'all') {
        return Object.values(retiredFacultyData).reduce((total, dept) => 
            total + getFilteredMembers(dept.members || []).length, 0);
    }
    return getFilteredMembers(retiredFacultyData[selectedDepartment]?.members || []).length;
  };

  return (
    <PageTemplate
      title={translations.title}
      subtitle={translations.subtitle}
      backgroundImage="/fenfakultesivideo.mp4"
      isVideo={true}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-8 mb-12"
          >
              <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-gray-600 to-gray-800 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Star className="w-8 h-8 text-white" />
                  </div>
                  <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-4">{translations.introTitle}</h2>
                      <div className="prose prose-gray max-w-none">
                          <p className="text-gray-700 mb-4">
                            {translations.introP1}
                          </p>
                          <p className="text-gray-700">
                            {translations.introP2}
                          </p>
                      </div>
                  </div>
              </div>
          </motion.div>

          <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
          >
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
                  <div className="flex items-center justify-between">
                      <div>
                          <p className="text-blue-100 text-sm">{translations.totalRetired}</p>
                          <p className="text-3xl font-bold">{getTotalCount()}</p>
                      </div>
                      <Users className="w-8 h-8 text-blue-200" />
                  </div>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6 text-white">
                  <div className="flex items-center justify-between">
                      <div>
                          <p className="text-green-100 text-sm">{translations.numDepartments}</p>
                          <p className="text-3xl font-bold">5</p>
                      </div>
                      <BookOpen className="w-8 h-8 text-green-200" />
                  </div>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-6 text-white">
                  <div className="flex items-center justify-between">
                      <div>
                          <p className="text-purple-100 text-sm">{translations.academicLegacy}</p>
                          <p className="text-3xl font-bold">∞</p>
                      </div>
                      <Award className="w-8 h-8 text-purple-200" />
                  </div>
              </div>
          </motion.div>

          <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8"
          >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                          type="text"
                          placeholder={translations.searchPlaceholder}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                  </div>
                  <div className="relative">
                      <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <select
                          value={selectedDepartment}
                          onChange={(e) => setSelectedDepartment(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all appearance-none bg-white"
                      >
                          {departments && departments.map(dept => (
                              <option key={dept.key} value={dept.key}>{dept.label}</option>
                          ))}
                      </select>
                  </div>
              </div>
              {searchTerm && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-blue-700 text-sm">
                          <strong>{getFilteredCount()}</strong> {translations.resultsFound} "{searchTerm}"
                      </p>
                  </div>
              )}
          </motion.div>

          <div className="space-y-8">
              {filteredDepartments.map(([deptKey, dept], deptIndex) => {
                  const IconComponent = departmentIcons[deptKey];
                  const filteredMembers = getFilteredMembers(dept.members);
                  
                  if (filteredMembers.length === 0) return null;

                  return (
                      <motion.div
                          key={deptKey}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 + deptIndex * 0.1 }}
                          className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
                      >
                          <div className={`bg-gradient-to-r ${dept.color} p-6 text-white`}>
                              <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                      {IconComponent && <IconComponent className="w-6 h-6" />}
                                  </div>
                                  <div>
                                      <h3 className="text-xl font-bold">{dept.title}</h3>
                                      <p className="text-white/80">
                                          {filteredMembers.length} {translations.retiredMember}
                                      </p>
                                  </div>
                              </div>
                          </div>
                          
                          <div className="p-6">
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                  {filteredMembers.map((member, index) => (
                                      <motion.div
                                          key={index}
                                          initial={{ opacity: 0, scale: 0.9 }}
                                          animate={{ opacity: 1, scale: 1 }}
                                          transition={{ delay: 0.4 + index * 0.05 }}
                                          className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-4 hover:shadow-md transition-all duration-300 border border-gray-100"
                                      >
                                          <div className="flex items-center gap-3">
                                              <div className="w-10 h-10 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                                                  <User className="w-5 h-5 text-gray-600" />
                                              </div>
                                              <div className="min-w-0">
                                                  <h4 className="font-medium text-gray-900 text-sm leading-tight">
                                                      {member}
                                                  </h4>
                                              </div>
                                          </div>
                                      </motion.div>
                                  ))}
                              </div>
                          </div>
                      </motion.div>
                  );
              })}
          </div>

          {getFilteredCount() === 0 && (
              <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-12"
              >
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Search className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{translations.noResultsTitle}</h3>
                  <p className="text-gray-600">
                      {translations.noResultsMessage}
                  </p>
              </motion.div>
          )}

          <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-8 text-center text-white mt-12"
          >
              <GraduationCap className="w-16 h-16 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-4">{translations.gratitudeTitle}</h3>
              <p className="text-blue-100 max-w-3xl mx-auto">
                  {translations.gratitudeMessage}
              </p>
          </motion.div>
      </div>
    </PageTemplate>
  );
};

export default RetiredFaculty;
